// src/components/DocGenerateDraftBar.js
import React from "react";

/**
 * DocGenerateDraftBar
 * - Shows "Generate Draft" (default) or "Apply Revision" (if overridden).
 * - Handles disabled + loading states, keyboard/ARIA accessible.
 *
 * Props:
 *  - disabled: boolean
 *  - onGenerate: () => void
 *  - isLoading?: boolean
 *  - noteText?: string
 *  - buttonLabel?: string         // default "Generate Draft" (Editor can set "Apply Revision")
 *  - loadingLabel?: string        // default "Generating…" (Editor can set "Applying…")
 *  - isLoggedIn?: boolean
 *  - onLoginRedirect?: () => void
 */
const DocGenerateDraftBar = ({
  disabled,
  onGenerate,
  isLoading = false,
  noteText = "You can change your subject once before your next full draft.",
  buttonLabel = "Generate Draft",
  loadingLabel = "Generating…",
  isLoggedIn = false,
  onLoginRedirect,
}) => {
  const s = {
    wrap: {
      borderTop: "1px solid #e5e7eb",
      paddingTop: 12,
      marginTop: 8,
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
    },
    button: {
      padding: "12px 16px",
      backgroundColor: disabled ? "#9bbcf5" : "#007bff",
      color: "#fff",
      border: "none",
      borderRadius: 8,
      fontSize: 16,
      fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      minWidth: 180,
      justifyContent: "center",
    },
    note: {
      fontSize: 12,
      color: "#6b7280",
      lineHeight: 1.4,
      flex: 1,
      minWidth: 220,
    },
    spinner: {
      width: 16,
      height: 16,
      border: "2px solid rgba(255,255,255,0.6)",
      borderTopColor: "#fff",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    kf: `
@keyframes spin { 
  to { transform: rotate(360deg); } 
}`,
  };

  const handleClick = () => {
    if (isLoading || disabled) return;

    if (!isLoggedIn && onLoginRedirect) {
      onLoginRedirect();
    } else if (isLoggedIn && onGenerate) {
      onGenerate();
    }
  };

  const renderLabel = () => {
    if (!isLoggedIn) return "Login";
    return isLoading ? loadingLabel : buttonLabel;
  };

  return (
    <div style={s.wrap} role="group" aria-label="Draft actions">
      <style>{s.kf}</style>

      <button
        type="button"
        onClick={handleClick}
        disabled={(isLoggedIn && (disabled || isLoading)) || false}
        aria-disabled={isLoggedIn ? disabled || isLoading : false}
        aria-busy={isLoading ? "true" : "false"}
        style={s.button}
      >
        {isLoggedIn && isLoading && <span style={s.spinner} aria-hidden="true" />}
        {renderLabel()}
      </button>

      <div style={s.note}>{noteText}</div>
    </div>
  );
};

export default DocGenerateDraftBar;
